import React, { useEffect, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Skeleton,
  Alert
} from "@mui/material";

import { getProjects } from "../../services/projectService";
import { useEmployees } from "../../context/EmployeesContext";
import DataTable from "../../ui/Table";

function ResourceCalendar({ employeeId, onCapacityChange }) {
  const { employees } = useEmployees();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCapacity, setTotalCapacity] = useState(0);

  const employee = employees.find((e) => e.employee_id === employeeId);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        if (employeeId) {
          const projectsData = await getProjects("", employeeId);
          setProjects(projectsData);

          const capacity = projectsData.reduce((sum, p) => sum + (p.capacity_percent || 0), 0);
          setTotalCapacity(Math.min(capacity, 100));

          if (onCapacityChange) {
            onCapacityChange(Math.min(capacity, 100));
          }
        }
      } catch (error) {
        console.error("Error loading calendar data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [employeeId, onCapacityChange]);

  const getCapacityColor = (capacity) => {
    if (capacity >= 100) return "#ef4444";
    if (capacity >= 75) return "#f97316";
    if (capacity >= 50) return "#eab308";
    return "#22c55e";
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "2-digit",
      month: "short",
      day: "numeric"
    });
  };

  // Single source of truth for the table so the loading skeleton always
  // matches the real column structure.
  const projectColumns = [
    {
      key: "project_name",
      label: "Project",
      render: (project) => (
        <Typography fontWeight={500} sx={{ fontSize: 13 }}>
          {project.project_name}
        </Typography>
      )
    },
    {
      key: "capacity_percent",
      label: "Capacity",
      align: "center",
      render: (project) => (
        <Chip
          label={`${project.capacity_percent || 0}%`}
          size="small"
          sx={{
            background: getCapacityColor(project.capacity_percent || 0),
            color: "#fff",
            fontWeight: 600,
            fontSize: 11
          }}
        />
      )
    },
    {
      key: "start_date",
      label: "Start",
      align: "center",
      render: (project) => (
        <Typography sx={{ fontSize: 13 }}>{formatDate(project.start_date)}</Typography>
      )
    },
    {
      key: "end_date",
      label: "End",
      align: "center",
      render: (project) => (
        <Typography sx={{ fontSize: 13 }}>{formatDate(project.end_date)}</Typography>
      )
    },
    {
      key: "status",
      label: "Status",
      align: "center",
      render: (project) => {
        const capacity = project.capacity_percent || 0;
        const isFullyBooked = capacity >= 100;

        return isFullyBooked ? (
          <Chip label="Fully Booked" size="small" color="error" variant="outlined" />
        ) : capacity > 0 ? (
          <Chip label="Active" size="small" color="success" variant="outlined" />
        ) : (
          <Chip label="Pending" size="small" variant="outlined" />
        );
      }
    }
  ];

  if (loading) {
    return (
      <Box>
        {/* Capacity card skeleton */}
        <Card sx={{ borderRadius: 2, mb: 2, bgcolor: "background.default" }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={160} height={28} sx={{ mt: 0.5 }} />
              </Box>
              <Skeleton variant="circular" width={60} height={60} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Skeleton variant="text" width={180} height={28} sx={{ mb: 1.5 }} />

            <DataTable
              columns={projectColumns}
              rows={[]}
              loading
              rowKey={(project) => project.project_id}
              pagination={false}
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!employeeId) {
    return (
      <Card sx={{ borderRadius: 2, bgcolor: "background.default" }}>
        <CardContent>
          <Typography color="textSecondary">
            Select an employee to view resource calendar
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card sx={{ borderRadius: 2, mb: 2, bgcolor: "background.default" }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                {employee?.employee_name || "Employee"}
              </Typography>
              <Typography variant="h6" fontWeight={600} mt={0.5}>
                Current Capacity
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    background: `conic-gradient(
                      ${getCapacityColor(totalCapacity)} 0deg ${(totalCapacity / 100) * 360}deg,
                      #e5e7eb ${(totalCapacity / 100) * 360}deg
                    )`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <Typography variant="h6" fontWeight={700} color={getCapacityColor(totalCapacity)}>
                    {totalCapacity}%
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">
                  {totalCapacity}% allocated
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {100 - totalCapacity}% available
                </Typography>
              </Box>
            </Box>
          </Box>

          {totalCapacity >= 100 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Employee is fully allocated (100% capacity)
            </Alert>
          )}
        </CardContent>
      </Card>

      {projects.length > 0 && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              Assigned Projects ({projects.length})
            </Typography>

            <DataTable
              columns={projectColumns}
              rows={projects}
              rowKey={(project) => project.project_id}
              pagination={false}
            />
          </CardContent>
        </Card>
      )}

      {projects.length === 0 && (
        <Card sx={{ borderRadius: 2, textAlign: "center", p: 3 }}>
          <Typography color="textSecondary">
            No projects assigned
          </Typography>
        </Card>
      )}
    </Box>
  );
}

export default ResourceCalendar;
